import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { gengql } from '@/__generated__';
import type { DashboardExpertRequestsQuery } from '@/__generated__/graphql';
import { isReviewable } from '@/consultation/reviewWindow';
import { canReviewConsultation } from '@/consultation/reviewer';
import { type UserContext, isGroupContext } from '@/core/user';
import { useAppSelector } from '@/store';

import { VALUE_FEEDBACK_FIELDS, safeNum } from './constants';
import type { ValueTagType } from './primitives/ValueTag';
import type { ResolvedRange } from './useDateRange';
import type { ValueTagCount } from './useValueDelivered';

// --- Context Detection ---

export type DashboardContext = 'credit-team' | 'smb-team' | 'expert' | 'client';

// Classification fields for the selected company. A subset of the viewer-groups
// shape, so a member group (from viewer.groups) and a group fetched by id (a
// staff/admin viewing a company they don't belong to) are interchangeable here.
export interface DashboardContextGroup {
  id: string;
  name?: string | null;
  slug?: string | null;
  billing_account?: { id?: string | null; isSMBAccount?: boolean | null } | null;
  capabilities?: { planType?: string | null } | null;
  internal_network?: { id: string; name?: string | null } | null;
}

// Selected company's classification, by id. Staff/admins can select a company
// they are NOT a member of, so it won't be in viewer.groups; the API authorizes
// staff to read any group's billing classification. ENG-5254.
const DASHBOARD_GROUP_CLASSIFICATION = gengql(/* GraphQL */ `
  query dashboardGroupClassification($groupId: String!) {
    group(id: $groupId) {
      id
      name
      slug
      billing_account {
        id
        isSMBAccount
      }
      capabilities {
        planType
      }
      internal_network {
        id
        name
      }
    }
  }
`);

// Resolves the selected group's classification. Member groups come from
// viewer.groups synchronously (no fetch); a non-member company is fetched by id.
// cache-first so it runs once per group per session and shares the normalized
// Group:id cache entry with useGroupStats.
function useDashboardGroup(groupId: string | undefined): {
  group: DashboardContextGroup | undefined;
  loading: boolean;
  error: boolean;
  isStaffViewer: boolean;
} {
  const viewerGroups = useAppSelector((state) => state.viewer.groups);
  const isAdmin = useAppSelector((state) => state.viewer.admin);
  const memberGroup = groupId ? (viewerGroups || []).find((g) => g?.id === groupId) : undefined;

  const needsFetch = !!groupId && !memberGroup;
  const { data, loading, error } = useQuery(DASHBOARD_GROUP_CLASSIFICATION, {
    variables: { groupId: groupId ?? '' },
    skip: !needsFetch,
    fetchPolicy: 'cache-first',
  });

  const group = (memberGroup ?? data?.group ?? undefined) as DashboardContextGroup | undefined;
  return {
    group,
    loading: needsFetch && loading && !group,
    // Only a genuine classification failure (fetch needed, errored, no group) so
    // the dashboard can show an error instead of silently degrading to the client
    // layout — which is the exact symptom this ticket fixes. ENG-5254.
    error: needsFetch && !group && !!error,
    // A platform admin viewing a company they're NOT a member of (so it had to be
    // fetched by id, not read from viewer.groups). Derived synchronously from
    // viewer state — independent of the roster query — so it never flips as
    // useViewerGroupRole's loading/skip/error states change. ENG-5254.
    isStaffViewer: !!isAdmin && needsFetch,
  };
}

export function useDashboardContext(): {
  dashCtx: DashboardContext;
  groupId: string | undefined;
  billingAccountId: string | undefined;
  canSeeBilling: boolean;
  group: DashboardContextGroup | undefined;
  classifying: boolean;
  classifyError: boolean;
  isStaffViewer: boolean;
} {
  const userContext = useAppSelector((state) => state.ui.userContext) as UserContext;
  const groupId = isGroupContext(userContext) ? userContext : undefined;
  const { group, loading, error, isStaffViewer } = useDashboardGroup(groupId);

  return useMemo(() => {
    if (userContext === 'expert') {
      return {
        dashCtx: 'expert' as const,
        groupId: undefined,
        billingAccountId: undefined,
        canSeeBilling: false,
        group: undefined,
        classifying: false,
        classifyError: false,
        isStaffViewer: false,
      };
    }

    if (groupId) {
      const hasBilling = !!group?.billing_account;
      const isSMB = group?.billing_account?.isSMBAccount === true;

      return {
        dashCtx: isSMB
          ? ('smb-team' as const)
          : hasBilling
            ? ('credit-team' as const)
            : ('client' as const),
        groupId,
        billingAccountId: group?.billing_account?.id ?? undefined,
        canSeeBilling: hasBilling,
        group,
        classifying: loading,
        classifyError: error,
        isStaffViewer,
      };
    }

    return {
      dashCtx: 'client' as const,
      groupId: undefined,
      billingAccountId: undefined,
      canSeeBilling: false,
      group: undefined,
      classifying: false,
      classifyError: false,
      isStaffViewer: false,
    };
  }, [userContext, groupId, group, loading, error, isStaffViewer]);
}

// --- GraphQL Queries ---

const DASHBOARD_CONSULTATIONS = gengql(/* GraphQL */ `
  query dashboardRecentConsultations($userContext: String!, $pageSize: Int!) {
    searchConsultations(
      states: [completed]
      first: $pageSize
      user_context: $userContext
      sort: "created_at"
      sort_order: "desc"
    ) {
      edges {
        node {
          id
          html_url
          state
          starts_at
          billing_duration
          engagement_type
          credits_used {
            cents
            currency
          }
          usd_charge_amount {
            cents
            currency
          }
          usd_charge_status
          expert {
            id
            name
            first_name
            last_name
            picture_url
            html_url
            headline
          }
          requester {
            id
            name
            first_name
            last_name
            picture_url
          }
          description
          expert_request {
            id
            name
            project {
              id
              name
            }
          }
          expert_review {
            id
            expert_usefulness_rating
            time_savings_feedback
            improved_bid_confidence_feedback
            partner_identification_feedback
            cost_savings_feedback
            new_opportunity_identification_feedback
          }
        }
      }
    }
  }
`);

const DASHBOARD_EXPERT_REQUESTS = gengql(/* GraphQL */ `
  query dashboardExpertRequests($groupId: String, $pageSize: Int!, $memberOnly: Boolean!) {
    searchProjects(
      first: $pageSize
      open_only: true
      member_only: $memberOnly
      group_id: $groupId
    ) {
      edges {
        node {
          id
          name
          html_url
          created_at
          expert_requests(state: open) {
            id
            name
            state
            html_url
            created_at
            stats {
              suggested: candidates_count(states: [suggested_by_research, suggested_by_platform])
              vetting: candidates_count(states: [vetting])
              verified: candidates_count(states: [verified])
              matched: candidates_count(states: [matched])
              calls
              completedCalls: calls(states: [completed])
              scheduledCalls: calls(states: [confirmed])
            }
          }
        }
      }
    }
  }
`);

// True total of the "View all projects" list (/expert_requests), which uses the
// same searchProjects resolver with open_only:false, member_only:false. Counted
// server-side so the My Projects badge matches that list instead of the capped,
// open+member-filtered preview below. ENG-5253.
const DASHBOARD_PROJECTS_TOTAL = gengql(/* GraphQL */ `
  query dashboardProjectsTotal($groupId: String) {
    searchProjects(first: 1, open_only: false, member_only: false, group_id: $groupId) {
      pageInfo {
        total
      }
    }
  }
`);

const DASHBOARD_SCHEDULED_CONSULTATIONS = gengql(/* GraphQL */ `
  query dashboardScheduledConsultations($userContext: String!) {
    searchConsultations(
      states: [confirmed, awaiting_expert_join]
      first: 200
      user_context: $userContext
    ) {
      edges {
        node {
          id
          expert {
            id
          }
        }
      }
    }
  }
`);

// --- Data Hook ---

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useDashboardConsultations(userContext: string) {
  const { data, loading, error } = useQuery(DASHBOARD_CONSULTATIONS, {
    variables: { userContext, pageSize: 30 },
    fetchPolicy: 'cache-and-network',
  });

  const consultations = useMemo(
    () => data?.searchConsultations?.edges?.map((e) => e?.node).filter(Boolean) ?? [],
    [data]
  );

  return { consultations, loading, error };
}

const DASHBOARD_EXPERTS_CONSULTED_COUNT = gengql(/* GraphQL */ `
  query dashboardExpertsConsultedCount($userContext: String!) {
    expertsConsultedCount(user_context: $userContext)
  }
`);

/**
 * Accurate count of distinct experts the viewer has consulted in the current
 * context. Backs the My Experts badge so it reflects all consulted experts
 * rather than only those derivable from the recent-consultations preview, which
 * is capped. ENG-5253.
 */
export function useDashboardExpertsConsultedCount(userContext: string): number {
  const { data } = useQuery(DASHBOARD_EXPERTS_CONSULTED_COUNT, {
    variables: { userContext },
    skip: !userContext,
    fetchPolicy: 'cache-and-network',
  });

  return data?.expertsConsultedCount ?? 0;
}

export function useScheduledByExpert(userContext: string): Map<string, number> {
  const { data } = useQuery(DASHBOARD_SCHEDULED_CONSULTATIONS, {
    variables: { userContext },
    fetchPolicy: 'cache-and-network',
  });

  return useMemo(() => {
    const map = new Map<string, number>();
    const edges = data?.searchConsultations?.edges ?? [];
    for (const edge of edges) {
      const expertId = edge?.node?.expert?.id;
      if (!expertId) continue;
      map.set(expertId, (map.get(expertId) ?? 0) + 1);
    }
    return map;
  }, [data]);
}

export type DashboardProject = NonNullable<
  NonNullable<NonNullable<DashboardExpertRequestsQuery['searchProjects']>['edges']>[number]['node']
>;

export type DashboardER = NonNullable<
  NonNullable<NonNullable<DashboardProject>['expert_requests']>[number]
>;

// memberOnly=false lets a staff/admin viewing a company they don't belong to see
// the company's active projects (matching the badge total) rather than an empty
// list. Members keep the default member-scoped preview. ENG-5254.
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useDashboardProjects(groupId: string | undefined, memberOnly = true) {
  const { data, loading, error } = useQuery(DASHBOARD_EXPERT_REQUESTS, {
    variables: { groupId, pageSize: 20, memberOnly },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });

  const projects = useMemo(() => {
    const edges = data?.searchProjects?.edges ?? [];
    return (edges.map((e) => e?.node).filter(Boolean) as DashboardProject[]).filter(
      (p) => p?.expert_requests && p.expert_requests.length > 0
    );
  }, [data]);

  return { projects, loading, error };
}

/**
 * Accurate total of the projects reachable via "View all projects"
 * (/expert_requests). Drives the My Projects badge so it matches that list
 * rather than the capped active-projects preview. ENG-5253.
 */
export function useDashboardProjectsTotal(groupId: string | undefined): number {
  const { data } = useQuery(DASHBOARD_PROJECTS_TOTAL, {
    variables: { groupId },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });

  return data?.searchProjects?.pageInfo?.total ?? 0;
}

// --- Derived: Your Experts ---

export interface ExpertSummary {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  pictureUrl: string | null | undefined;
  htmlUrl: string | null | undefined;
  headline: string | null;
  consultationCount: number;
  scheduledCount: number;
  /** Total cost in display cents (USD or OFC pip per dashboardCurrency(dashCtx)). */
  costTotalCents: number;
  lastConsultedAt: string | null | undefined;
  avgRating: number | null;
  projectNames: string[];
  consultations: ExpertConsultation[];
}

export interface ExpertConsultation {
  id: string;
  htmlUrl: string | null | undefined;
  title: string;
  date: string | null | undefined;
  durationMinutes: number | null;
  /** Cost in display cents (USD or OFC pip per dashboardCurrency(dashCtx)). */
  costCents: number;
  engagementType: string | null | undefined;
  projectName: string | null;
  rating: number | null;
  hasReview: boolean;
  /** Whether the viewer may review this consultation — its requester or expert (ENG-5259). */
  canReview: boolean;
  valueTags: string[];
}

/**
 * Cost currency for a given dashboard context.
 * SMB dashboards display USD; everyone else displays OFC credits.
 */
export function dashboardCurrency(dashCtx: DashboardContext): 'USD' | 'OFC' {
  return dashCtx === 'smb-team' ? 'USD' : 'OFC';
}

/**
 * Per-consultation cost in display cents (USD or OFC pip), depending on
 * dashboard context. SMB pulls usd_charge_amount; everyone else pulls
 * credits_used. Aggregations and render code stay currency-agnostic and
 * delegate currency-aware formatting to Money.formatted() at the leaves.
 */
function consultationCostCents(
  c:
    | {
        credits_used?: { cents: string | null } | null;
        usd_charge_amount?: { cents: string | null } | null;
      }
    | null
    | undefined,
  dashCtx: DashboardContext
): number {
  const raw = dashCtx === 'smb-team' ? c?.usd_charge_amount?.cents : c?.credits_used?.cents;
  return raw ? parseInt(raw, 10) || 0 : 0;
}

function extractValueTags(review: Record<string, unknown> | null | undefined): string[] {
  if (!review) return [];
  const tags: string[] = [];
  for (const { field, label } of VALUE_FEEDBACK_FIELDS) {
    if (review[field] === true) tags.push(label);
  }
  return tags;
}

export function useYourExperts(
  consultations: ReturnType<typeof useDashboardConsultations>['consultations'],
  scheduledByExpert: Map<string, number> | undefined,
  dashCtx: DashboardContext,
  viewerId: string | null | undefined
): ExpertSummary[] {
  return useMemo(() => {
    interface ExpertAccumulator {
      id: string;
      name: string;
      firstName: string;
      lastName: string;
      pictureUrl: string | null | undefined;
      htmlUrl: string | null | undefined;
      headline: string | null;
      consultationCount: number;
      costTotalCents: number;
      lastConsultedAt: string | null | undefined;
      ratingSum: number;
      ratedCount: number;
      projectNames: Set<string>;
      consultations: ExpertConsultation[];
    }

    const byExpert = new Map<string, ExpertAccumulator>();

    for (const c of consultations) {
      const expert = c?.expert;
      if (!expert?.id) continue;

      const rating = c?.expert_review?.expert_usefulness_rating ?? null;
      const projectName = c?.expert_request?.project?.name ?? null;
      const costCents = consultationCostCents(c, dashCtx);
      const consultation: ExpertConsultation = {
        id: c.id,
        htmlUrl: c.html_url,
        title: c.description ?? c.expert_request?.name ?? 'Consultation',
        date: c.starts_at,
        durationMinutes: c.billing_duration ?? null,
        costCents,
        engagementType: c.engagement_type,
        projectName,
        rating,
        hasReview: !!c.expert_review,
        canReview: canReviewConsultation(viewerId, c),
        valueTags: extractValueTags(c.expert_review as Record<string, unknown>),
      };

      const existing = byExpert.get(expert.id);
      if (existing) {
        existing.consultationCount += 1;
        existing.costTotalCents += costCents;
        if (c?.starts_at && (!existing.lastConsultedAt || c.starts_at > existing.lastConsultedAt)) {
          existing.lastConsultedAt = c.starts_at;
        }
        if (rating !== null) {
          existing.ratingSum += rating;
          existing.ratedCount += 1;
        }
        if (projectName) existing.projectNames.add(projectName);
        existing.consultations.push(consultation);
      } else {
        const projects = new Set<string>();
        if (projectName) projects.add(projectName);
        byExpert.set(expert.id, {
          id: expert.id,
          name: expert.name ?? `${expert.first_name ?? ''} ${expert.last_name ?? ''}`.trim(),
          firstName: expert.first_name ?? '',
          lastName: expert.last_name ?? '',
          pictureUrl: expert.picture_url,
          htmlUrl: expert.html_url,
          headline: expert.headline ?? null,
          consultationCount: 1,
          costTotalCents: costCents,
          lastConsultedAt: c?.starts_at,
          ratingSum: rating ?? 0,
          ratedCount: rating !== null ? 1 : 0,
          projectNames: projects,
          consultations: [consultation],
        });
      }
    }

    return Array.from(byExpert.values())
      .map(({ ratingSum, ratedCount, projectNames, ...rest }) => ({
        ...rest,
        avgRating: ratedCount > 0 ? ratingSum / ratedCount : null,
        projectNames: Array.from(projectNames),
        scheduledCount: scheduledByExpert?.get(rest.id) ?? 0,
        consultations: rest.consultations.sort((a, b) =>
          (b.date ?? '').localeCompare(a.date ?? '')
        ),
      }))
      .sort((a, b) => (b.lastConsultedAt ?? '').localeCompare(a.lastConsultedAt ?? ''));
  }, [consultations, scheduledByExpert, dashCtx, viewerId]);
}

// --- Derived: Experts Grouped by Project ---

// useExpertsByProject removed — My Experts card now uses useYourExperts.

// --- Derived: Cost Per Project ---

/**
 * Returns cost per project, in display cents (USD or OFC pip per
 * dashboardCurrency(dashCtx)). Render via Money(cents, currency).formatted().
 */
export function useCostByProjectCents(
  consultations: ReturnType<typeof useDashboardConsultations>['consultations'],
  dashCtx: DashboardContext
): Map<string, number> {
  return useMemo(() => {
    const map = new Map<string, number>();
    for (const c of consultations) {
      const projectId = c?.expert_request?.project?.id;
      if (!projectId) continue;
      const cents = consultationCostCents(c, dashCtx);
      if (cents === 0) continue;
      map.set(projectId, (map.get(projectId) ?? 0) + cents);
    }
    return map;
  }, [consultations, dashCtx]);
}

// --- Derived: Project Stats ---

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useProjectStats(projects: DashboardProject[]) {
  return useMemo(() => {
    let attentionCount = 0;
    for (const p of projects) {
      const hasVerified = (p?.expert_requests ?? []).some(
        (er: DashboardER) => safeNum(er.stats?.verified) > 0
      );
      if (hasVerified) attentionCount += 1;
    }
    return { attentionCount };
  }, [projects]);
}

// --- Group Stats (server-side aggregates, date-range aware) ---
//
// project/request/expert counts are a live snapshot (no date filter). The
// consultation-derived metrics honor stats(start_date, end_date) and default to
// all time when no range is passed (preserving the pre-redesign call site).
// ENG-5256 drops the prior-art `previous`/@include(withPrevious) compare
// machinery — the redesign has no compare overlay.

const DASHBOARD_GROUP_STATS = gengql(/* GraphQL */ `
  query dashboardGroupStats($groupId: String!, $startDate: Datetime, $endDate: Datetime) {
    group(id: $groupId) {
      id
      stats(start_date: $startDate, end_date: $endDate) {
        project_count
        expert_request_count
        consultation_count
        expert_count
        average_expert_usefulness_rating
        rated_consultation_count
        consultations_completed
        consultations_reviewed
        consultations_pending
        consultations_rated
        average_rating
        projects_engaged
        projects_created
        experts_engaged
        expert_requests_created
        credits_spent {
          cents
          currency
        }
        consultation_spend {
          cents
          currency
        }
        value_delivered {
          time_savings
          bid_confidence
          partner_id
          cost_savings
          new_opportunities
        }
        consultation_timeseries {
          period_start
          total
          reviewed
          pending
          credits_spent {
            cents
          }
          consultation_spend {
            cents
          }
          average_rating
          projects
          experts
        }
        expert_requests_created_timeseries {
          period_start
          count
        }
        projects_created_timeseries {
          period_start
          count
        }
      }
    }
  }
`);

/** Client-side shape of a `consultation_timeseries` bucket from group.stats. */
export interface ConsultationBucket {
  /** RFC3339 inclusive start of the bucket. */
  periodStart: string;
  total: number;
  reviewed: number;
  pending: number;
  /** Credits spent in the bucket, in OFC pip (cents). */
  creditsCents: number;
  /** USD captured on the bucket's consultations, in USD cents (SMB). */
  consultationSpendCents: number;
  /** Mean expert rating in the bucket; null for empty buckets. */
  averageRating: number | null;
  projects: number;
  experts: number;
}

/** Client-side shape of an `expert_requests_created_timeseries` bucket from group.stats. */
export interface ExpertRequestBucket {
  /** RFC3339 inclusive start of the bucket. */
  periodStart: string;
  count: number;
}

/** Client-side shape of a `projects_created_timeseries` bucket from group.stats. */
export interface ProjectBucket {
  /** RFC3339 inclusive start of the bucket. */
  periodStart: string;
  count: number;
}

export interface GroupStats {
  // Live snapshot (no date filter) — backs pre-redesign consumers.
  projectCount: number;
  requestCount: number;
  expertCount: number;
  consultationCount: number;
  avgRating: number | null;
  ratedCount: number;
  // Range-aware aggregates (ENG-5256). Honor the optional date range; default to
  // all time when no range is passed.
  consultationsCompleted: number;
  consultationsReviewed: number;
  consultationsPending: number;
  ratedCountInRange: number;
  averageRating: number | null;
  projectsEngaged: number;
  /** Projects created in range (by created_at). */
  projectsCreated: number;
  expertsEngaged: number;
  expertRequests: number;
  /** Credits spent in range, in OFC pip (cents). */
  creditsSpentCents: number;
  /** USD spent on consultations in range, in USD cents (SMB; excludes subscriptions). */
  consultationSpendCents: number;
  /** Range-filtered Value Delivered, adapted to ValueTag's kebab-case shape. */
  valueDelivered: ValueTagCount[];
  consultationTimeseries: ConsultationBucket[];
  expertRequestsTimeseries: ExpertRequestBucket[];
  projectsCreatedTimeseries: ProjectBucket[];
  loading: boolean;
  error: boolean;
  /** Underlying query error, for Sentry reporting. Null when no error. */
  errorDetail: Error | null;
  /** Re-runs the group-stats query — wired to the card's retry affordance. */
  refetch: () => void;
}

function parseCents(money: { cents?: string | null } | null | undefined): number {
  const raw = money?.cents;
  if (!raw) return 0;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Adapts the API's `value_delivered` object (5 named Int fields, snake_case) to
 * the `ValueTagCount[]` the ValueTag consumer expects (kebab-case `type`).
 * Drops zero-count categories and sorts by descending count so the most
 * impactful value lands first. Exported for the Account Overview consumer + tests.
 */
export function adaptValueDelivered(
  vd:
    | {
        time_savings?: number | null;
        bid_confidence?: number | null;
        partner_id?: number | null;
        cost_savings?: number | null;
        new_opportunities?: number | null;
      }
    | null
    | undefined
): ValueTagCount[] {
  if (!vd) return [];
  const mapping: { key: keyof NonNullable<typeof vd>; type: ValueTagType }[] = [
    { key: 'time_savings', type: 'time-savings' },
    { key: 'bid_confidence', type: 'bid-confidence' },
    { key: 'partner_id', type: 'partner-id' },
    { key: 'cost_savings', type: 'cost-savings' },
    { key: 'new_opportunities', type: 'new-opportunities' },
  ];
  return mapping
    .map(({ key, type }) => ({ type, count: vd[key] ?? 0 }))
    .filter((tag) => tag.count > 0)
    .sort((a, b) => b.count - a.count);
}

export function useGroupStats(groupId: string | undefined, range?: ResolvedRange): GroupStats {
  const { data, loading, error, refetch } = useQuery(DASHBOARD_GROUP_STATS, {
    variables: {
      groupId: groupId ?? '',
      startDate: range?.start_date ?? null,
      endDate: range?.end_date ?? null,
    },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });

  const stats = data?.group?.stats;
  return {
    projectCount: stats?.project_count ?? 0,
    requestCount: stats?.expert_request_count ?? 0,
    expertCount: stats?.expert_count ?? 0,
    consultationCount: stats?.consultation_count ?? 0,
    avgRating: stats?.average_expert_usefulness_rating ?? null,
    ratedCount: stats?.rated_consultation_count ?? 0,
    consultationsCompleted: stats?.consultations_completed ?? 0,
    consultationsReviewed: stats?.consultations_reviewed ?? 0,
    consultationsPending: stats?.consultations_pending ?? 0,
    ratedCountInRange: stats?.consultations_rated ?? 0,
    averageRating: stats?.average_rating ?? null,
    projectsEngaged: stats?.projects_engaged ?? 0,
    projectsCreated: stats?.projects_created ?? 0,
    expertsEngaged: stats?.experts_engaged ?? 0,
    expertRequests: stats?.expert_requests_created ?? 0,
    creditsSpentCents: parseCents(stats?.credits_spent),
    consultationSpendCents: parseCents(stats?.consultation_spend),
    valueDelivered: adaptValueDelivered(stats?.value_delivered),
    consultationTimeseries: (stats?.consultation_timeseries ?? []).map((b) => ({
      periodStart: b.period_start,
      total: b.total,
      reviewed: b.reviewed,
      pending: b.pending,
      creditsCents: parseCents(b.credits_spent),
      consultationSpendCents: parseCents(b.consultation_spend),
      averageRating: b.average_rating ?? null,
      projects: b.projects,
      experts: b.experts,
    })),
    expertRequestsTimeseries: (stats?.expert_requests_created_timeseries ?? []).map((b) => ({
      periodStart: b.period_start,
      count: b.count,
    })),
    projectsCreatedTimeseries: (stats?.projects_created_timeseries ?? []).map((b) => ({
      periodStart: b.period_start,
      count: b.count,
    })),
    loading,
    error: !!error,
    errorDetail: error ?? null,
    refetch: () => {
      void refetch();
    },
  };
}

// --- Derived: Unreviewed Count ---

export function useUnreviewedCount(
  consultations: ReturnType<typeof useDashboardConsultations>['consultations'],
  viewerId: string | null | undefined
): number {
  return useMemo(() => {
    return consultations.filter(
      (c) => !c?.expert_review && isReviewable(c?.starts_at) && canReviewConsultation(viewerId, c)
    ).length;
  }, [consultations, viewerId]);
}
